import { lazy, Suspense, useEffect, useState } from "react";
import { HashRouter, Route, Routes, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "@/app/store";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { logout, restoreAuth } from "@/features/auth/authSlice";
import { SuperAdminLayout } from "@/layouts/SuperAdminLayout";
import { OperationalLayout } from "@/layouts/OperationalLayout";
import { StaffLayout } from "@/layouts/StaffLayout";
import { RoleGuard } from "@/components/layout/RoleGuard";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import api from "@/services/api";
// import CallLogsPage from "./features/agents/telecaller/CallLogsPage";
// import CallDetailsPage from "./features/agents/telecaller/CallDetailPage";

// Auth
const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const ResetPasswordPage = lazy(
  () => import("@/features/auth/ResetPasswordPage"),
);

// Admin pages
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const NotificationsPage = lazy(
  () => import("@/features/notifications/NotificationsPage"),
);
const EMIDashboardPage = lazy(
  () => import("@/features/dashboard/EMIDashboardPage"),
);
const DebtorsPage = lazy(() => import("@/features/debtors/DebtorsPage"));
const DebtorDetailPage = lazy(
  () => import("@/features/debtors/DebtorDetailPage"),
);
const CasesPage = lazy(() => import("@/features/cases/CasesPage"));
const CaseDetailPage = lazy(() => import("@/features/cases/CaseDetailPage"));
const PaymentsPage = lazy(() => import("@/features/payments/PaymentsPage"));
const DisputesPage = lazy(() => import("@/features/disputes/DisputesPage"));
const AgentsPage = lazy(() => import("@/features/agents/AgentsPage"));
const AgentDetailPage = lazy(() => import("@/features/agents/AgentDetailPage"));
const EmployeesPage = lazy(() => import("@/features/hrm/EmployeesPage"));
const AttendancePage = lazy(() => import("@/features/hrm/AttendancePage"));
const SalaryIncentivesPage = lazy(
  () => import("@/features/salary-incentives/SalaryIncentivesPage"),
);
const IncentiveConfigurationsPage = lazy(
  () =>
    import("@/features/incentive-configurations/IncentiveConfigurationsPage"),
);
const BankManagementPage = lazy(
  () => import("@/features/settings/BankManagementPage"),
);
const TargetManagementPage = lazy(
  () => import("@/features/settings/TargetManagementPage"),
);
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const RegionManagementPage = lazy(
  () => import("@/features/settings/RegionManagementPage"),
);
const PermissionManagementPage = lazy(
  () => import("@/features/settings/PermissionManagementPage"),
);
const LevelDesignationPage = lazy(
  () => import("@/features/settings/LevelDesignationPage"),
);

// Super Admin pages
const SuperAdminDashboard = lazy(
  () => import("@/features/superadmin/SuperAdminDashboard"),
);
const CompaniesPage = lazy(() => import("@/features/superadmin/CompaniesPage"));
const SubscriptionsPage = lazy(
  () => import("@/features/superadmin/SubscriptionsPage"),
);
const SuperAdminReportsPage = lazy(
  () => import("@/features/superadmin/SuperAdminReportsPage"),
);
const FeatureTogglesPage = lazy(
  () => import("@/features/superadmin/FeatureTogglesPage"),
);
const AuditLogsPage = lazy(() => import("@/features/superadmin/AuditLogsPage"));

// Agent pages
const AgentPortalPage = lazy(() => import("@/features/agents/AgentPortalPage"));
const AgentCasesPage = lazy(() => import("@/features/agents/AgentCasesPage"));
const DailyTasksPage = lazy(() => import("@/features/agents/DailyTasksPage"));
const AgentPerformancePage = lazy(
  () => import("@/features/agents/AgentPerformancePage"),
);

// imports
const CallLogsPage = lazy(
  () => import("@/features/agents/telecaller/CallLogsPage"),
);
const CallDetailsPage = lazy(
  () => import("@/features/agents/telecaller/CallDetailPage"),
);

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

function AuthBootstrapper({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const verifyToken = async () => {
      if (!token) {
        if (!cancelled) setReady(true);
        return;
      }

      try {
        const response = await api.get("/v1/auth/me");
        if (cancelled) return;

        const nextUser = response.data?.user;
        if (nextUser) {
          dispatch(
            restoreAuth({
              user: nextUser
                ? {
                    id: String(nextUser.id),
                    name: nextUser.name,
                    email: nextUser.email,
                    role: nextUser.role,
                  }
                : user!,
              token,
              permissions,
            }),
          );
        }
      } catch {
        if (!cancelled) {
          dispatch(logout());
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    void verifyToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!ready) {
    return <Loading />;
  }

  return <>{children}</>;
}

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <HashRouter>
          <AuthBootstrapper>
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/" element={<Navigate to="/login" replace />} />

                {/* Super Admin Portal */}
                <Route
                  element={
                    <RoleGuard allowedRoles={["super_admin"]}>
                      <SuperAdminLayout />
                    </RoleGuard>
                  }
                >
                  <Route
                    path="/super-admin"
                    element={<SuperAdminDashboard />}
                  />
                  <Route
                    path="/super-admin/companies"
                    element={<CompaniesPage />}
                  />
                  <Route
                    path="/super-admin/subscriptions"
                    element={<SubscriptionsPage />}
                  />
                  <Route
                    path="/super-admin/reports"
                    element={<SuperAdminReportsPage />}
                  />
                  <Route
                    path="/super-admin/features"
                    element={<FeatureTogglesPage />}
                  />
                  <Route
                    path="/super-admin/audit"
                    element={<AuditLogsPage />}
                  />
                  <Route
                    path="/super-admin/settings"
                    element={<SettingsPage />}
                  />
                  <Route
                    path="/super-admin/notifications"
                    element={<NotificationsPage />}
                  />
                  <Route path="/super-admin/calls" element={<CallLogsPage />} />
                  <Route
                    path="/super-admin/calls/:callId"
                    element={<CallDetailsPage />}
                  />
                </Route>

                {/* Staff Portal */}
                <Route
                  element={
                    <RoleGuard
                      allowedRoles={["admin", "manager", "team_leader"]}
                    >
                      <StaffLayout />
                    </RoleGuard>
                  }
                >
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route
                    path="/dashboard/emi"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <EMIDashboardPage />
                      </RoleGuard>
                    }
                  />
                  <Route path="/debtors" element={<DebtorsPage />} />
                  <Route path="/debtors/:id" element={<DebtorDetailPage />} />
                  <Route path="/cases" element={<CasesPage />} />
                  <Route path="/cases/:id" element={<CaseDetailPage />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                  <Route
                    path="/disputes"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <DisputesPage />
                      </RoleGuard>
                    }
                  />
                  <Route path="/agents" element={<AgentsPage />} />
                  <Route path="/agents/:id" element={<AgentDetailPage />} />
                  <Route
                    path="/hrm/employees"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <EmployeesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/hrm/attendance"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <AttendancePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/hrm/salary-incentives"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <SalaryIncentivesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/hrm/incentive-configurations"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <IncentiveConfigurationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route
                    path="/settings"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <SettingsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/settings/regions"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <RegionManagementPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/settings/banks"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <BankManagementPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/settings/targets"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <TargetManagementPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/settings/permissions"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <PermissionManagementPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/settings/levels-designations"
                    element={
                      <RoleGuard allowedRoles={["admin"]}>
                        <LevelDesignationPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="/admin/notifications"
                    element={<NotificationsPage />}
                  />
                  <Route path="/admin/calls" element={<CallLogsPage />} />
                  <Route
                    path="/admin/calls/:callId"
                    element={<CallDetailsPage />}
                  />
                </Route>

                <Route
                  element={
                    <RoleGuard allowedRoles={["agent", "telecaller"]}>
                      <OperationalLayout />
                    </RoleGuard>
                  }
                >
                  <Route path="/agent-portal" element={<AgentPortalPage />} />
                  <Route
                    path="/agent-portal/cases"
                    element={<AgentCasesPage />}
                  />
                  <Route
                    path="/agent-portal/cases/:id"
                    element={<CaseDetailPage />}
                  />
                  <Route
                    path="/agent-portal/calls"
                    element={<CallLogsPage />}
                  />
                  <Route
                    path="/agent-portal/calls/:debtorId"
                    element={<CallDetailsPage />}
                  />
                  <Route path="/agents/:id" element={<AgentDetailPage />} />
                  <Route
                    path="/agent-portal/tasks"
                    element={<DailyTasksPage />}
                  />
                  <Route
                    path="/agent-portal/performance"
                    element={<AgentPerformancePage />}
                  />
                  <Route
                    path="/agent-portal/notifications"
                    element={<NotificationsPage />}
                  />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </AuthBootstrapper>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </Provider>
);

export default App;
